import { useMemo } from "react";
import useStockedVariationsData from "@/features/productVariations/hooks/useStockedVariationsData";
import useInStockVariations from "@/features/productVariations/hooks/useInStockVariations";
import useAttributeSelection from "@/features/productVariations/hooks/useAttributeSelection";
import useAttributeOptions from "@/features/productVariations/hooks/useAttributeOptions";
import useVariationMatching from "@/features/productVariations/hooks/useVariationMatching";
import useDisplayPrices from "@/features/productVariations/hooks/useDisplayPrices";
import type {
  AvailableTermsSetMap,
  DisplayPrices,
  SelectedTerms,
} from "@/features/productVariations/types";
import { ProductType, type Product } from "@/types/products";
import type { ProductVariation } from "@/types/productVariations";

interface ProductAttributesViewModel {
  isVariationsLoading: boolean;
  selectedTerms: SelectedTerms;
  availableTermsSetMap: AvailableTermsSetMap;
  matchedVariation: ProductVariation | null;
  displayPrices: DisplayPrices;
  handleAttributeSelect: (attributeName: string, termName: string) => void;
  getMatchedVariationId: () => number | null;
}

/**
 * 商品變體管理 Hook
 *
 * 職責：組合所有變體相關的小 hooks，提供統一的 API
 */
const useProductVariations = (product: Product | null): ProductAttributesViewModel => {
  // 1. 取得變體資料
  const { stockedVariations, isVariationsLoading } = useStockedVariationsData(product);

  // 2. 過濾有庫存的變體
  const inStockVariations = useInStockVariations(stockedVariations);

  // 取得屬性名稱列表
  const attributeNames = useMemo(
    () => product?.attributes?.map((attr) => attr.name) || [],
    [product?.attributes],
  );

  // 3. 計算初始可用選項（用於預設選項設定）
  const initialAvailableTermsSetMap: AvailableTermsSetMap = useMemo(() => {
    if (product?.type !== ProductType.Variable || !attributeNames) return {};

    const result: AvailableTermsSetMap = {};
    for (const attrName of attributeNames) {
      result[attrName] = new Set<string>();
    }

    inStockVariations.forEach((variation) => {
      variation.attributes.forEach((attr) => {
        if (result[attr.name]) {
          result[attr.name].add(attr.option);
        }
      });
    });

    return result;
  }, [product?.type, attributeNames, inStockVariations]);

  // 4. 管理屬性選擇
  const { selectedTerms, handleAttributeSelect } = useAttributeSelection(
    product,
    stockedVariations,
    inStockVariations,
    initialAvailableTermsSetMap,
  );

  // 5.  動態可用選項計算
  const availableTermsSetMap = useAttributeOptions(
    initialAvailableTermsSetMap,
    selectedTerms,
    attributeNames,
    inStockVariations,
  );

  // 6. 匹配變體
  const { matchedVariation, getMatchedVariationId } = useVariationMatching(
    inStockVariations,
    selectedTerms,
  );

  // 7. 計算價格
  const displayPrices = useDisplayPrices(product, matchedVariation);

  return {
    isVariationsLoading,
    selectedTerms,
    availableTermsSetMap,
    matchedVariation,
    displayPrices,
    handleAttributeSelect,
    getMatchedVariationId,
  };
};

export default useProductVariations;
